import React, { useReducer, useContext, Dispatch } from 'react';
import ErrorSnackBar from '../../components/app/ErrorSnackBar/ErrorSnackBar';
import { AlertContextAction, AlertContextState, ErrorSnackBarParams } from './AlertContext.interface';
import getUniqueId from './uniqueAlertId.helper';

const reducer: (state: AlertContextState, action: AlertContextAction) => AlertContextState = (state, action) => {
    switch (action.type) {
        case "add":
            return {
                ...state,
                alerts: [
                    ...state.alerts,
                    { ...action.payload, id: getUniqueId() }
                ]
            };
        case "remove": {
            const alerts = [];
            let needRedirect = false;
            state.alerts.forEach(alert => {
                if (alert.id !== action.payload) {
                    alerts.push(alert);
                } else {
                    needRedirect = alert.type === "auth";
                }
            });

            return {
                ...state,
                alerts: state.alerts.filter(alert => alert.id !== action.payload),
                needRedirect: needRedirect || state.needRedirect

            };
        }
        case "redirect":
            return {
                ...state,
                needRedirect: true,
                redirectUrl: action.payload || ''
            }
        default:
            throw new Error(`Invalid action ${action}`);
    }
};

export const AlertContext = React.createContext({});
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const AlertDispatchContext = React.createContext<Dispatch<AlertContextAction>>(null!);
AlertContext.displayName = "AlertContext";
const initialState = {
    alerts: [],
    needRedirect: false,
    redirectUrl: ''
};

export const AlertProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
    const [state, dispatch] = useReducer(reducer, initialState);

    return (
        <AlertContext.Provider value={state}>
            <AlertDispatchContext.Provider value={dispatch}>
                {
                    state.alerts.map(({ id, ...other }: ErrorSnackBarParams) => (
                        <ErrorSnackBar
                            {...other}
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            key={id}
                            onClose={() => dispatch({ type: "remove", payload: id })}
                            open
                        />
                    ))
                }
                {state.needRedirect &&
                    <meta httpEquiv="Refresh" content={`0; url='${state.redirectUrl || '/#/login'}'`} />
                }
                {children}
            </AlertDispatchContext.Provider>
        </AlertContext.Provider>
    );
}

export const useAlertDispatch: () => Dispatch<AlertContextAction> = () => {
    const alertDispatch = useContext(AlertDispatchContext);
    return alertDispatch;
}
